import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { HMIField, HMIGroupMeta, HMILiveValues, HMILogEntry, HMITemplate } from '../types';
import {
  buildScreenChain,
  DEFAULT_SECTION_LAYOUT,
  leafScreenKey,
  ROOT_SCREEN_KEY,
  screenHasContent,
} from '../layoutUtils';
import { CommandResult, useHmiLiveChannel } from '../useHmiLiveChannel';
import { deleteRecipe, listRecipes, RecipeDocument, saveRecipe } from '../store';
import GroupCard from './GroupCard';
import LogViewer from './LogViewer';
import ProcessRecipeDialog, { ProcessRecipeDialogMode } from './ProcessRecipeDialog';

/** Maps each builder-configurable `actionBehavior` to the file-picker mode it opens (see `types.ts`). */
const RECIPE_DIALOG_MODES: Partial<Record<NonNullable<HMIField['actionBehavior']>, ProcessRecipeDialogMode>> = {
  load_process: 'load',
  save_process: 'save',
  delete_process: 'delete',
};

export interface HMIRendererProps {
  template: HMITemplate;
  twinId?: string;
  /** Route to the HMI Configuration page, pre-selecting this template. Ignored when `onConfigure` is provided. */
  settingsHref?: string;
  /** Switches the host page to its "HMI Configuration" tab (the Asset Hierarchy sibling tab) instead of navigating. */
  onConfigure?: () => void;
  canConfigure?: boolean;
  /**
   * Shows every tab/subtab, including ones with no sections/fields yet.
   * The live Digital Twin > HMI tab leaves this off so operators never see
   * empty tabs; the layout builder's preview panel turns it on so a newly
   * added tab/subtab/section shows up immediately, before any fields exist.
   */
  showAllScreens?: boolean;
}

function prettifyKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface ScreenBodyProps {
  activeScreen: string;
  isProcessScreen: boolean;
  logs: HMILogEntry[];
  screenFields: HMIField[];
  /** Every field in the template - threaded through to `kind: "image"` fields, whose pins can reference fields in any section. */
  allFields: HMIField[];
  nonHeaderGroups: string[];
  groupOrder: string[];
  groupMeta: (key: string) => HMIGroupMeta | undefined;
  effectiveGroup: string | undefined;
  groupLabel: (key: string) => string;
  values: HMILiveValues;
  disabledFieldIds: Set<string>;
  onCommit: (field: HMIField, value: unknown) => Promise<CommandResult>;
  onRunAction: (field: HMIField) => Promise<CommandResult>;
  onSelectGroup: (key: string) => void;
}

/** Picks the right layout for the active screen (log viewer / process master-detail / stacked cards). */
function ScreenBody(props: ScreenBodyProps) {
  const {
    activeScreen,
    isProcessScreen,
    logs,
    screenFields,
    allFields,
    nonHeaderGroups,
    groupOrder,
    groupMeta,
    effectiveGroup,
    groupLabel,
    values,
    disabledFieldIds,
    onCommit,
    onRunAction,
    onSelectGroup,
  } = props;

  if (activeScreen === 'log') {
    return <LogViewer logs={logs} />;
  }

  if (isProcessScreen) {
    return (
      <Box display="flex" gap={2}>
        <Box minWidth={180}>
          <GroupCard
            title={groupLabel('header')}
            fields={screenFields.filter((f) => f.group === 'header')}
            allFields={allFields}
            values={values}
            disabledFieldIds={disabledFieldIds}
            onCommit={onCommit}
            onRunAction={onRunAction}
          />
          <Stack spacing={0.5}>
            {nonHeaderGroups.map((g) => (
              <Chip
                key={g}
                label={groupLabel(g)}
                color={effectiveGroup === g ? 'primary' : 'default'}
                onClick={() => onSelectGroup(g)}
                sx={{ justifyContent: 'flex-start' }}
              />
            ))}
          </Stack>
        </Box>
        <Box flexGrow={1}>
          <GroupCard
            title={groupLabel(effectiveGroup ?? '')}
            fields={screenFields.filter((f) => f.group === effectiveGroup)}
            allFields={allFields}
            values={values}
            disabledFieldIds={disabledFieldIds}
            onCommit={onCommit}
            onRunAction={onRunAction}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {groupOrder.map((g) => {
        const layout = groupMeta(g)?.layout ?? DEFAULT_SECTION_LAYOUT;
        return (
          <Grid
            key={g}
            item
            xs={12}
            md={layout.width}
            sx={{
              display: 'flex',
              '& > *': { width: '100%', minHeight: layout.height * 50 },
            }}
          >
            <GroupCard
              title={groupLabel(g)}
              fields={screenFields.filter((f) => f.group === g)}
              allFields={allFields}
              values={values}
              disabledFieldIds={disabledFieldIds}
              onCommit={onCommit}
              onRunAction={onRunAction}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}

/**
 * Generic, schema-driven renderer for a `HMITemplate`. Contains zero
 * BMT-specific logic - every screen/group/field comes from the template, so
 * a different equipment vendor's HMI only requires a new template document,
 * never a code change.
 */
function HMIRenderer({
  template,
  twinId,
  settingsHref,
  onConfigure,
  canConfigure,
  showAllScreens,
}: HMIRendererProps) {
  const channel = useHmiLiveChannel(template, twinId);

  // Remembers which tab is active at each level of the tab/subtab tree (see
  // `HmiLayoutBuilder`, which authors this same tree) so a tab with subtabs
  // just navigates deeper instead of showing content of its own.
  const [selection, setSelection] = useState<Record<string, string>>({});

  const chain = useMemo(
    () =>
      buildScreenChain(
        template,
        selection,
        (s) => showAllScreens || s.key === 'log' || screenHasContent(template, s.key)
      ),
    [template, selection, showAllScreens]
  );
  const activeScreen = leafScreenKey(chain) ?? 'dashboard';

  const groupLabel = (key: string) =>
    template.groups.find((g) => g.key === key)?.label ?? prettifyKey(key);
  const groupMeta = (key: string) => template.groups.find((g) => g.key === key);

  const screenFields = template.fields.filter((f) => f.screen === activeScreen);
  const groupOrder = useMemo(() => {
    const order = new Map<string, number>();
    screenFields.forEach((f) => {
      if (!order.has(f.group) || (order.get(f.group) as number) > f.order) {
        order.set(f.group, f.order);
      }
    });
    return Array.from(order.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([key]) => key);
  }, [screenFields]);

  const isProcessScreen = activeScreen === 'process';
  const nonHeaderGroups = groupOrder.filter((g) => g !== 'header');
  const [activeGroup, setActiveGroup] = useState<string | undefined>(undefined);
  const effectiveGroup = activeGroup ?? nonHeaderGroups[0];

  const [recipes, setRecipes] = useState<RecipeDocument[]>([]);
  const [recipeDialogMode, setRecipeDialogMode] = useState<ProcessRecipeDialogMode>('load');
  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  // The action field that opened the dialog - tells us which screen/section
  // to target (content fields) and which field (if any) shows the file name.
  const [recipeActionField, setRecipeActionField] = useState<HMIField | undefined>();

  const refreshRecipes = () => {
    listRecipes().then(setRecipes);
  };

  const openRecipeDialog = (mode: ProcessRecipeDialogMode, actionField: HMIField) => {
    setRecipeDialogMode(mode);
    setRecipeActionField(actionField);
    refreshRecipes();
    setRecipeDialogOpen(true);
  };

  // Every other field on the same screen as the action button, excluding its
  // own section (so a "file management" header can sit next to the
  // parameters it loads/saves/clears without wiping/saving itself).
  const contentFieldsFor = (actionField: HMIField) =>
    template.fields.filter((f) => f.screen === actionField.screen && f.group !== actionField.group);

  // The text control (if any) in the action button's own section used to
  // display/edit the currently loaded file's name, e.g. "Process File Name".
  const nameFieldFor = (actionField: HMIField) =>
    template.fields.find(
      (f) =>
        f.screen === actionField.screen &&
        f.group === actionField.group &&
        f.kind === 'control' &&
        f.datatype === 'string'
    );

  const recipeNameField = recipeActionField ? nameFieldFor(recipeActionField) : undefined;
  const recipeDefaultName =
    recipeNameField && typeof channel.values[recipeNameField.id] === 'string'
      ? (channel.values[recipeNameField.id] as string)
      : undefined;

  const handleCommit = (field: Parameters<typeof channel.setFieldValue>[0], value: unknown) =>
    channel.setFieldValue(field, value);

  const handleRunAction = (field: Parameters<typeof channel.runAction>[0]) => {
    const dialogMode = field.actionBehavior ? RECIPE_DIALOG_MODES[field.actionBehavior] : undefined;
    if (dialogMode) {
      openRecipeDialog(dialogMode, field);
      return Promise.resolve<CommandResult>({ ok: true });
    }
    if (field.actionBehavior === 'clear_process') {
      channel.resetFields(contentFieldsFor(field), `${field.caption} completed.`);
      const nameField = nameFieldFor(field);
      if (nameField) channel.applyValues({ [nameField.id]: '' });
      return Promise.resolve<CommandResult>({ ok: true });
    }
    return channel.runAction(field);
  };

  const handleLoadRecipe = (recipe: RecipeDocument) => {
    const nameField = recipeActionField ? nameFieldFor(recipeActionField) : undefined;
    channel.applyValues(
      { ...(recipe.values as HMILiveValues), ...(nameField ? { [nameField.id]: recipe.name } : {}) },
      `Loaded process file "${recipe.name}" (${Object.keys(recipe.values).length} parameter(s)).`
    );
    setRecipeDialogOpen(false);
  };

  const handleSaveRecipe = async (name: string) => {
    if (!recipeActionField) return;
    const targets = contentFieldsFor(recipeActionField);
    const values: Record<string, unknown> = {};
    targets.forEach((field) => {
      values[field.id] = channel.values[field.id];
    });
    await saveRecipe(name, values);
    const nameField = nameFieldFor(recipeActionField);
    channel.applyValues(nameField ? { [nameField.id]: name } : {}, `Saved process file "${name}".`);
    refreshRecipes();
    setRecipeDialogOpen(false);
  };

  const handleDeleteRecipe = async (name: string) => {
    await deleteRecipe(name);
    refreshRecipes();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        {canConfigure && onConfigure && (
          <Button
            size="small"
            onClick={onConfigure}
            startIcon={<SettingsIcon fontSize="small" />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Configure HMI
          </Button>
        )}
        {canConfigure && !onConfigure && settingsHref && (
          <Link
            component={RouterLink}
            to={settingsHref}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
          >
            <SettingsIcon fontSize="small" /> Configure HMI
          </Link>
        )}
      </Stack>

      {/* Vertical tab rail(s) - one column per nesting level (tab, then its subtabs, ...), beside the content. */}
      <Stack direction="row" alignItems="flex-start" spacing={0}>
        {chain.map((level) => (
          <Tabs
            key={level.parentKey ?? ROOT_SCREEN_KEY}
            orientation="vertical"
            variant="scrollable"
            value={level.activeKey}
            onChange={(_, v) => {
              setSelection((prev) => ({ ...prev, [level.parentKey ?? ROOT_SCREEN_KEY]: v }));
              setActiveGroup(undefined);
            }}
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              minWidth: level.parentKey ? 140 : 160,
              flexShrink: 0,
            }}
          >
            {level.options.map((s) => (
              <Tab
                key={s.key}
                value={s.key}
                label={s.label}
                sx={{
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  minHeight: level.parentKey ? 36 : 48,
                  py: level.parentKey ? 0.5 : undefined,
                }}
              />
            ))}
          </Tabs>
        ))}

        <Box sx={{ flexGrow: 1, minWidth: 0, pl: 2 }}>
          <ScreenBody
            activeScreen={activeScreen}
            isProcessScreen={isProcessScreen}
            logs={channel.logs}
            screenFields={screenFields}
            allFields={template.fields}
            nonHeaderGroups={nonHeaderGroups}
            groupOrder={groupOrder}
            effectiveGroup={effectiveGroup}
            groupMeta={groupMeta}
            groupLabel={groupLabel}
            values={channel.values}
            disabledFieldIds={channel.disabledFieldIds}
            onCommit={handleCommit}
            onRunAction={handleRunAction}
            onSelectGroup={setActiveGroup}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mt: 2, pt: 1, borderTop: '1px solid rgba(0,0,0,0.08)' }}
      >
        <Typography variant="caption" color="text.secondary">
          Template: {template.name} (v{template.version})
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Poll interval: {template.pollIntervalSec}s
        </Typography>
        {channel.lastPolledAt && (
          <Typography variant="caption" color="text.secondary">
            Last poll: {new Date(channel.lastPolledAt).toLocaleTimeString()}
          </Typography>
        )}
        <Chip
          size="small"
          label={channel.isPolling ? 'Polling...' : 'Live'}
          color={channel.isPolling ? 'warning' : 'success'}
          variant="outlined"
        />
      </Stack>

      <ProcessRecipeDialog
        open={recipeDialogOpen}
        mode={recipeDialogMode}
        recipes={recipes}
        defaultName={recipeDefaultName}
        onClose={() => setRecipeDialogOpen(false)}
        onLoad={handleLoadRecipe}
        onSave={handleSaveRecipe}
        onDelete={handleDeleteRecipe}
      />
    </Box>
  );
}

export default HMIRenderer;
